import React, { useEffect, useState } from "react";
import { collection, getDocs, doc, setDoc, deleteDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";
import { Plus, Trash2, FileText } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { PROGRAMS } from "../ProgramsPage";

const AdminAssignments = () => {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", courseId: PROGRAMS[0].id, deadline: "" });

  const load = async () => {
    setLoading(true);
    const snap = await getDocs(collection(db, "assignments"));
    setAssignments(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.title) { toast.error("Title required"); return; }
    const id = `ASSIGN${Date.now()}`;
    await setDoc(doc(db, "assignments", id), { ...form, assignmentId: id, status: "active", createdAt: serverTimestamp() });
    toast.success("Assignment created");
    setForm({ title: "", description: "", courseId: PROGRAMS[0].id, deadline: "" });
    setDialogOpen(false);
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this assignment?")) return;
    await deleteDoc(doc(db, "assignments", id));
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid="admin-assignments">
      <div className="mb-8 flex items-end justify-between">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-2">Assignments</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Manage Assignments</h1>
          <p className="mt-1 text-slate-600">{assignments.length} assignments</p>
        </div>
        <button onClick={() => setDialogOpen(true)} data-testid="add-assignment-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold">
          <Plus className="w-4 h-4" /> Add Assignment
        </button>
      </div>

      {loading ? (
        <div className="text-slate-500">Loading...</div>
      ) : assignments.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center" data-testid="no-assignments-admin">
          <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No assignments yet. Create your first one.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {assignments.map((a) => (
            <div key={a.id} className="bg-white border border-slate-200 rounded-xl p-5 flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <div className="font-heading font-semibold text-slate-900">{a.title}</div>
                <div className="text-sm text-slate-600 mt-1">{a.description}</div>
                <div className="mt-2 text-xs text-slate-500">Course: <span className="font-medium text-slate-700">{a.courseId}</span> · Deadline: {a.deadline || "—"}</div>
              </div>
              <button onClick={() => remove(a.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-md">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Add Assignment</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-4">
            <div>
              <label className="block text-xs text-slate-600 font-medium mb-1">Title</label>
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-600 focus:outline-none" />
            </div>
            <div>
              <label className="block text-xs text-slate-600 font-medium mb-1">Description</label>
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-600 focus:outline-none" />
            </div>
            <div>
              <label className="block text-xs text-slate-600 font-medium mb-1">Course</label>
              <select value={form.courseId} onChange={(e) => setForm({ ...form, courseId: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-600 focus:outline-none">
                {PROGRAMS.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-600 font-medium mb-1">Deadline</label>
              <input type="date" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-600 focus:outline-none" />
            </div>
            <button onClick={add} data-testid="save-assignment-btn" className="w-full px-4 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold">
              Create Assignment
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminAssignments;
